import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import StatusBadge from "../../../components/RestaurantAdminComponents/StatusBadge";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import eyeIcon from "../../../assets/images/user-dashboard/eye.svg";

/* -------------------------------------------------------------------------
 * CustomersPage — full "Customers" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const RESTAURANT_CUSTOMERS_ENDPOINT = "/api/restaurants/customers";
const DEFAULT_SORT_BY = "most_spend";
const DEFAULT_CUSTOMER_TYPE = "";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const columns = (handleViewDetail) => [
  {
    key: "customer",
    label: "Customer",
    width: "1.6fr",
    render: (r) => (
      <span className="rp-table__row-label">
        <div className="rp-avatar rp-avatar--placeholder">
          {r.name ? r.name.charAt(0).toUpperCase() : '?'}
        </div>
        <div>
          <div>{r.name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{r.email}</div>
        </div>
      </span>
    ),
  },
  { key: "email", label: "Email", width: "1.4fr" },
  { key: "type", label: "Type", width: "0.8fr", render: (r) => <StatusBadge label={r.type} /> },
  { key: "totalSpend", label: "Total Spend", width: "1fr", render: (r) => r.totalSpendDisplay },
  { key: "totalVisits", label: "Total Visits", width: "0.8fr", align: "right" },
  { key: "firstVisit", label: "First Visit", width: "1fr", render: (r) => r.firstVisit || 'N/A' },
  { key: "lastVisit", label: "Last Visit", width: "1fr", render: (r) => r.lastVisit || 'N/A' },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <img
        src={eyeIcon}
        alt="View"
        onClick={() => handleViewDetail(row)}
        style={{ cursor: 'pointer', width: '20px', height: '20px' }}
      />
    ),
  },
];

const CustomersPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Customers | Omaha Food Lovers";
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [customerType, setCustomerType] = useState(DEFAULT_CUSTOMER_TYPE);
  const latestRequestRef = useRef(0);
  const clearAllCandidateRef = useRef(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Get restaurant ID from localStorage
  useEffect(() => {
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        setRestaurantId(user.restaurant_id || user.vendor_listing_id || user.id);
      } catch (e) {
        console.error('Failed to parse authUser:', e);
      }
    }
  }, []);

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    if (customerType) {
      params.append("type", customerType);
    }

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const fetchCustomers = async (pageNum = 1, isLoadMore = false) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${RESTAURANT_CUSTOMERS_ENDPOINT}?${buildQueryParams(pageNum)}`);
      if (requestId !== latestRequestRef.current) return;

      if (response.data?.success) {
        const data = response.data.data;
        
        // Extract restaurant info from response
        if (data.restaurant && !isLoadMore) {
          setRestaurantId(data.restaurant.id);
          setRestaurantName(data.restaurant.title);
        }
        
        const paginationData = data.customers && typeof data.customers === "object" && !Array.isArray(data.customers)
          ? data.customers
          : data;
        const customersArray = Array.isArray(paginationData?.data)
          ? paginationData.data
          : Array.isArray(data.customers)
            ? data.customers
            : [];
        const newCustomers = customersArray.map(customer => ({
          id: customer.id,
          name: customer.name || customer.full_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown',
          email: customer.email || 'N/A',
          avatarUrl: customer.avatar_url || customer.profile_image || null,
          type: customer.customer_type_label || customer.customer_type || customer.status_label || customer.status || 'Unknown',
          totalSpend: customer.total_spend || 0,
          totalSpendDisplay: customer.total_spend_display || formatMoney(customer.total_spend),
          totalVisits: customer.visits || customer.total_visits || 0,
          firstVisit: formatDate(customer.first_visit_at),
          lastVisit: formatDate(customer.last_visit_at),
        }));

        if (isLoadMore) {
          setCustomers((prev) => [...prev, ...newCustomers]);
        } else {
          setCustomers(newCustomers);
        }

        const currentPage = paginationData?.current_page || pageNum;
        const lastPage = paginationData?.last_page;
        setHasMore(
          typeof lastPage !== "undefined"
            ? currentPage < lastPage
            : customersArray.length >= 20
        );
        setPage(currentPage);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Failed to fetch customers:", error);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchCustomers(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
    setCustomers([]);
  };

  const resetTable = () => {
    setPage(1);
    setHasMore(true);
    setCustomers([]);
  };

  const handleSortChange = (value) => {
    setSortBy(value || DEFAULT_SORT_BY);
    resetTable();

    if ((value || DEFAULT_SORT_BY) === DEFAULT_SORT_BY) {
      clearAllCandidateRef.current = true;
      setTimeout(() => {
        clearAllCandidateRef.current = false;
      }, 0);
    }
  };

  const handleCustomerTypeChange = (value) => {
    const nextType = value || DEFAULT_CUSTOMER_TYPE;
    setCustomerType(nextType);
    resetTable();

    if (nextType === DEFAULT_CUSTOMER_TYPE && clearAllCandidateRef.current) {
      setSearch("");
    }
  };


  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.restaurantExport}?type=customers`, {
        responseType: 'blob',
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_export.csv');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toastr.success('Customers exported successfully');
    } catch (error) {
      console.error('Failed to export customers:', error);
      toastr.error('Failed to export customers');
    }
  };

  const handleViewDetail = (row) => {
    const queryParams = new URLSearchParams({
      customer_id: row.id,
      restaurant_id: restaurantId || '',
      merchant_name: restaurantName || '',
    });
    navigate(`/restaurant/customers/detail?${queryParams.toString()}`);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: handleSortChange,
      defaultValue: DEFAULT_SORT_BY,
      options: [
        { value: "most_spend", label: "Most Spend" },
        { value: "most_visits", label: "Most Visits" },
      ],
    },
    {
      key: "customerType",
      type: "sortBy",
      label: "Customer Type",
      value: customerType,
      onChange: handleCustomerTypeChange,
      defaultValue: DEFAULT_CUSTOMER_TYPE,
      options: [
        { value: "", label: "All Types" },
        { value: "new", label: "New" },
        { value: "returning", label: "Returning" },
      ],
    },
  ];

  useEffect(() => {
    fetchCustomers(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, customerType]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Customers</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of customer profiles and spending activity.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name"
            filters={filters}
            showExport
            onExport={handleExport}
          />

        <DataTable
          columns={columns(handleViewDetail)}
          rows={customers}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>
    </div>
  );
};

CustomersPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

CustomersPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default CustomersPage;
